import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from '@/store';
import { refreshSession } from '@/services/authService';
import { tokensRefreshed, signOut } from '@/store/slices/authSlice';
import { API_BASE_URL, isBackendConfigured } from '@/config/api';

// Resolved at runtime from the Metro dev-server host in dev — see src/config/api.ts. Re-exported
// here so existing importers keep working.
export { API_BASE_URL, isBackendConfigured };

function isRetryableNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  const status = (error as { status: unknown }).status;
  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
}

const rawBaseQuery = retry(
  fetchBaseQuery({
    baseUrl: API_BASE_URL || 'https://backend-not-configured.invalid',
    // The API host sleeps when idle and can take over a minute to wake; 20s per try with three
    // retries (reads only, below) rides out most wake-ups instead of showing "Couldn't load".
    timeout: 20_000,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set('authorization', `Bearer ${token}`);
      headers.set('accept', 'application/json');
      return headers;
    },
  }),
  {
    // Only reads are retried: repeating a timed-out booking, payment or other write could apply
    // it twice when the first attempt did reach the server.
    retryCondition: (error, args, { attempt }) =>
      attempt <= 3 && isRetryableNetworkError(error) && (typeof args === 'string' || !args.method || args.method.toUpperCase() === 'GET'),
  },
);

// Access tokens are short-lived (15 min — see service-auth). On a 401, refresh once using the
// stored refresh token and retry the original request, instead of leaving every call broken
// until the user manually logs back in. Concurrent 401s share one in-flight refresh.
let refreshPromise: ReturnType<typeof refreshSession> | null = null;

// The shop lists a customer browses. When they have a gender filter on, these ask only for shops that serve it.
const GENDER_FILTERED = /^\/tenants\/(nearby|popular|salons|recommended|banners|search)(\?|$)/;

function withGenderFilter(args: string | FetchArgs, api: { getState: () => unknown }): string | FetchArgs {
  const state = api.getState() as RootState;
  const gender = state.preferences?.genderFilter;
  if (!gender) return args;
  const url = typeof args === 'string' ? args : args.url;
  if (!GENDER_FILTERED.test(url) || url.includes('forGender=')) return args;
  const next = `${url}${url.includes('?') ? '&' : '?'}forGender=${gender}`;
  return typeof args === 'string' ? next : { ...args, url: next };
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  rawArgs,
  api,
  extraOptions,
) => {
  const args = withGenderFilter(rawArgs, api);
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const refreshToken = (api.getState() as RootState).auth.refreshToken;
    if (refreshToken) {
      try {
        refreshPromise = refreshPromise ?? refreshSession(refreshToken);
        const tokens = await refreshPromise;
        api.dispatch(tokensRefreshed(tokens));
        result = await rawBaseQuery(args, api, extraOptions);
      } catch {
        api.dispatch(signOut());
      } finally {
        refreshPromise = null;
      }
    }
  }

  return result;
};

// Empty base — every real endpoint is added via `.injectEndpoints()` in a domain-specific file
// under store/api/ (businessApi.ts, appointmentApi.ts, loyaltyApi.ts, etc.), all sharing this one
// auth-retrying base query, cache, and tag system.
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['CustomerHistory', 'Appointment', 'Favorite', 'Loyalty', 'Tenant', 'UserProfile', 'CatalogService', 'MyReviews', 'Notification', 'NotificationPreference', 'Ticket', 'Referral', 'Conversation', 'Blocks', 'Address'],
  // Paired with setupListeners(store.dispatch) in store/index.ts — without both, RTK Query never
  // refetches on app foreground/reconnect, so a stylist's newly added service wouldn't show up in
  // the customer app until the 60s cache window happened to expire.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});
